import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hydrateUiState, setUiLoggedIn } from "@/lib/uiState";

const ThemeContext = createContext(null);
const ACCENTS = ["navy", "crimson", "forest", "ocean"];

function apply(theme, accent) {
  const root = document.documentElement;
  const dark = theme === "dark";
  root.classList.toggle("dark", dark);
  root.setAttribute("data-accent", ACCENTS.includes(accent) ? accent : "navy");
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState(() => localStorage.getItem("ud_theme") || "system");
  const [accent, setAccentState] = useState(() => localStorage.getItem("ud_accent") || "navy");

  useEffect(() => { apply(theme, accent); }, [theme, accent]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const h = () => {
      if ((localStorage.getItem("ud_theme") || "system") === "system") {
        apply("system", localStorage.getItem("ud_accent") || "navy");
      }
    };
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  // Adopt the user's saved preferences (theme, accent + panel/UI state) once they load.
  useEffect(() => {
    setUiLoggedIn(!!(user && user.id));
    if (user && user.prefs) {
      const t = user.prefs.theme || "system";
      const a = user.prefs.accent || "navy";
      setThemeState(t); setAccentState(a);
      localStorage.setItem("ud_theme", t); localStorage.setItem("ud_accent", a);
      hydrateUiState(user.prefs.ui);
    }
  }, [user]);

  const persist = useCallback((t, a) => {
    localStorage.setItem("ud_theme", t); localStorage.setItem("ud_accent", a);
    if (user && user.id) api.put("/auth/preferences", { theme: t, accent: a }).catch(() => {});
  }, [user]);

  const setTheme = (t) => { setThemeState(t); persist(t, accent); };
  const setAccent = (a) => { setAccentState(a); persist(theme, a); };

  return <ThemeContext.Provider value={{ theme, accent, setTheme, setAccent }}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
